import { Link } from "react-router-dom";
import { Shield } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-cyan-400" aria-hidden="true" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-[#CBD5E1]">Last updated: May 8, 2026</p>
          <p className="text-[#CBD5E1] text-sm mt-1">NimbleNode | Owner: Mimmo Gallo | Jurisdiction: Canada (Ontario)</p>
        </div>

        <div className="space-y-10 text-[#CBD5E1] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using NimbleNode ("the Platform", "we", "us", or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Platform. These Terms constitute a legally binding agreement between you and NimbleNode.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Session Recording & Analytics</h2>
            <p className="p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-cyan-200">
              By using NimbleNode, you agree that your session activity may be recorded for product improvement and analytics purposes using Microsoft Clarity. Microsoft Clarity may capture mouse movements, clicks, scrolls, and other interactions. Data collected is subject to Microsoft's privacy policy. You may opt out by contacting us at support@nimblenode.io.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Description of Service</h2>
            <p>NimbleNode is an AI-powered content creation platform that enables users to generate images, videos, audio, and other media using artificial intelligence. The Platform also provides tools for social media scheduling, analytics, marketplace features, and design editing.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Account Registration</h2>
            <p>You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You must be at least 18 years old to use the Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Permitted Use</h2>
            <p className="mb-3">You may use the Platform only for lawful purposes and in accordance with these Terms. You agree to use the Platform to create content you have the legal right to create and publish.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Prohibited Conduct</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Generate content that is illegal, harmful, threatening, abusive, harassing, defamatory, or obscene</li>
              <li>Generate content that infringes on intellectual property rights of others</li>
              <li>Create deepfakes or non-consensual intimate imagery</li>
              <li>Use the Platform to generate CSAM or any content involving minors in a sexual context</li>
              <li>Attempt to reverse engineer, hack, or disrupt the Platform</li>
              <li>Use automated bots or scrapers to access the Platform</li>
              <li>Share your account credentials with third parties</li>
              <li>Use the Platform for any fraudulent or deceptive purposes</li>
              <li>Violate any applicable Canadian, provincial, or international laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Intellectual Property</h2>
            <p className="mb-3">The Platform and its original content, features, and functionality are owned by NimbleNode and are protected by Canadian and international intellectual property laws.</p>
            <p>Content you generate using the Platform: You retain ownership of the outputs you create, subject to the licenses granted to us to operate the service. You are solely responsible for ensuring your prompts and generated content do not infringe third-party rights.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Credits & Payments</h2>
            <p className="mb-3">Access to AI generation features requires credits. Credits are purchased through the Platform and are non-refundable except as required by applicable law. Subscription fees are billed in advance and are non-refundable except within the stated money-back guarantee period.</p>
            <p>We reserve the right to change pricing with 30 days' notice to active subscribers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Disclaimers</h2>
            <p className="mb-3">THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>
            <p>We do not warrant that the Platform will be uninterrupted, error-free, or that generated content will meet your expectations. AI-generated content may be inaccurate, incomplete, or offensive despite safety filters.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE CANADIAN LAW, NIMBLENODE AND ITS OWNERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE PLATFORM. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Termination</h2>
            <p>We reserve the right to suspend or terminate your account at any time for violation of these Terms, without prior notice. You may terminate your account at any time by contacting us at support@nimblenode.io.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Ontario, Canada.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Changes to Terms</h2>
            <p>We may update these Terms from time to time. We will notify you of material changes by email or through the Platform. Your continued use of the Platform after such notice constitutes acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">14. Contact</h2>
            <p>For questions about these Terms, please contact us at: <a href="mailto:support@nimblenode.io" className="text-cyan-400 hover:underline">support@nimblenode.io</a></p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-16 pt-8 border-t border-[#334155] flex flex-wrap gap-4 justify-center text-sm text-[#CBD5E1]">
          <Link to="/" className="hover:text-cyan-400 transition-colors">← Back to Home</Link>
          <Link to="/PrivacyPolicy" className="hover:text-cyan-400 transition-colors">Privacy Policy</Link>
          <Link to="/Support" className="hover:text-cyan-400 transition-colors">Support</Link>
        </div>
      </div>
    </div>
  );
}